import React, { useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { withOktaAuth } from "@okta/okta-react";
import { createStructuredSelector } from "reselect";
import { DownOutlined, LogoutOutlined } from "@ant-design/icons";
import { Col, Collapse, Dropdown, Menu, Row, Space, Switch, Typography } from "antd";

import ACTIONS from "../store/action";
import { selectUser, selectIsFetching, selectIDPConfig, selectNotificationPreferences } from "../store/selector";
import { ApiNames } from "../api/api";
import { logout } from "@tailormed/common-client/services/auth";
import { oktaConfig } from "../constant/config";
import { USER_ROLES } from "../constant/user";

import "./HeaderMenu.css";

const { Text } = Typography;
const { Panel } = Collapse;

const weeklyTriggerName = "pharmacy_create_emails_for_orders_weekly";
const dailyTriggerName = "pharmacy_create_emails_for_orders_daily";

const HeaderMenuComponent = ({
  t,
  user,
  isFetching,
  setUserEmailConfigurationAct,
  updateNotificationPreferences,
  notificationPreferences,
  idpConfig,
  authState
}) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [currentFrequencyUpdate, setCurrentFrequencyUpdate] = useState(null);

  const handleClick = (e) => {
    const { key } = e;

    if (key === menuKeys.LOGOUT) {
      idpConfig && logout(user, idpConfig, oktaConfig.loginURI);
    }
  };

  const handleEmailConfigurationChange = (triggerName, active) => {
    setUserEmailConfigurationAct({ triggerName, active });
    setCurrentFrequencyUpdate(triggerName);
  };

  const handleOpenFundsNotificationChange = (checked) => {
    updateNotificationPreferences("Global", 0, "email", checked);
  };

  const openFundsNotificationsEnabled =
    notificationPreferences?.globalPreferences?.email !== undefined
      ? notificationPreferences.globalPreferences.email
      : false;

  const menuKeys = {
    EMAIL_CONFIGURATIONS: "emailConfiguration",
    LOGOUT: "logout"
  };

  const signoutMenueItem = idpConfig ? (
    <Menu.Item key={menuKeys.LOGOUT}>
      <Space>
        <LogoutOutlined />
        <Text>{t("header_menu.sign_out")}</Text>
      </Space>
    </Menu.Item>
  ) : (
    <></>
  );

  const menu = (
    <Menu onClick={(e) => handleClick(e)}>
      {authState?.isAuthenticated &&
        user && [
          <Menu.Item key={menuKeys.EMAIL_CONFIGURATIONS}>
            <Collapse ghost>
              <Panel
                header={t("header_menu.email_configuraion.title")}
                className="header-menu-panel"
                key="1"
                showArrow={false}
              >
                <Col>
                  <Row justify="space-between" gutter={[50, 0]}>
                    <Col>
                      <Text>{t("header_menu.email_configuraion.weekly")}</Text>
                    </Col>
                    <Col>
                      <Switch
                        size="small"
                        checked={
                          user &&
                          user.emailConfiguration &&
                          (!user.emailConfiguration[weeklyTriggerName] ||
                            user.emailConfiguration[weeklyTriggerName].active)
                        }
                        disabled={currentFrequencyUpdate === weeklyTriggerName && isFetching}
                        onChange={(active) => handleEmailConfigurationChange(weeklyTriggerName, active)}
                      />
                    </Col>
                  </Row>
                  <Row justify="space-between" gutter={[50, 0]}>
                    <Col>
                      <Text>{t("header_menu.email_configuraion.daily")}</Text>
                    </Col>
                    <Col>
                      <Switch
                        size="small"
                        checked={
                          user &&
                          user.emailConfiguration &&
                          (!user.emailConfiguration[dailyTriggerName] ||
                            user.emailConfiguration[dailyTriggerName].active)
                        }
                        disabled={currentFrequencyUpdate === dailyTriggerName && isFetching}
                        onChange={(active) => handleEmailConfigurationChange(dailyTriggerName, active)}
                      />
                    </Col>
                  </Row>
                  {user?.role === USER_ROLES.PHARMACOLOGIST && (
                    <Row justify="space-between" gutter={[50, 0]}>
                      <Col>
                        <Text>{t("header_menu.email_configuraion.program_open")}</Text>
                      </Col>
                      <Col>
                        <Switch
                          size="small"
                          checked={openFundsNotificationsEnabled}
                          onChange={handleOpenFundsNotificationChange}
                        />
                      </Col>
                    </Row>
                  )}
                </Col>
              </Panel>
            </Collapse>
          </Menu.Item>,
          signoutMenueItem
        ]}
    </Menu>
  );

  return (
    <Dropdown
      trigger="click"
      placement="bottomRight"
      overlay={menu}
      open={menuVisible && authState?.isAuthenticated}
      onOpenChange={(visible) => setMenuVisible(visible)}
    >
      <Space>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 40,
            width: 40,
            borderRadius: "50%",
            backgroundColor: "white"
          }}
        >
          <Text strong style={{ color: "#2B56A3" }}>
            {`${user ? user.firstName.substring(0, 1) : ""} ${user ? user.lastName.substring(0, 1) : ""}`}
          </Text>
        </div>
        <DownOutlined style={{ color: "white" }} />
      </Space>
    </Dropdown>
  );
};

const mapStateToProps = createStructuredSelector({
  user: selectUser,
  isFetching: selectIsFetching([ApiNames.EmailConfiguration]),
  notificationPreferences: selectNotificationPreferences,
  idpConfig: selectIDPConfig
});

const mapDispatchToProps = (dispatch) => ({
  setUserEmailConfigurationAct: ({ triggerName, active }) =>
    dispatch(ACTIONS.setUserEmailConfigurationAction({ triggerName, active })),
  updateNotificationPreferences: (preferenceType, targetId, notificationType, isEnabled) =>
    dispatch(ACTIONS.updateNotificationPreferencesAction(preferenceType, targetId, notificationType, isEnabled))
});

const HeaderMenu = compose(
  withOktaAuth,
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(HeaderMenuComponent);

export { HeaderMenu };
